import { useEffect, useState } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PanelContent from '@shared/components/PanelContent';
import { usePortSearch } from '../hooks/usePortSearch';
import { useCreateRouteAtoB } from '../hooks/useCreateRouteAtoB';
import { useMapStore } from '@shared/hooks/useMapStore';
import type { Port } from '@shared/types';
import type { RouteCreationOptions } from '../types';

interface Props {
  onBack: () => void;
}

export default function RouteCreationPanel({ onBack }: Props) {
  const [departurePort, setDeparturePort] = useState<Port | null>(null);
  const [arrivalPort, setArrivalPort] = useState<Port | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [routeName, setRouteName] = useState('');
  const [options, setOptions] = useState<RouteCreationOptions>({
    allowSuezCanal: true,
    allowPanamaCanal: true,
    allowBeringStrait: false,
    allowRivers: false,
  });
  const [isEditingName, setIsEditingName] = useState(false);
  const [optionsExpanded, setOptionsExpanded] = useState(false);
  const [focusedPortId, setFocusedPortId] = useState<number | null>(null);

  const { data: ports = [], isLoading: portsLoading } = usePortSearch(searchQuery);
  const createRoute = useCreateRouteAtoB();
  const setPanToCoordinate = useMapStore((s) => s.setPanToCoordinate);
  const setDeparturePortId = useMapStore((s) => s.setDeparturePortId);
  const setArrivalPortId = useMapStore((s) => s.setArrivalPortId);

  const autoName =
    departurePort && arrivalPort ? `${departurePort.name} - ${arrivalPort.name}` : '';

  const optionLabels: Record<keyof RouteCreationOptions, string> = {
    allowSuezCanal: 'Suez',
    allowPanamaCanal: 'Panama',
    allowBeringStrait: 'Bering',
    allowRivers: 'Rivers',
  };

  const selectedOptionsLabel =
    (Object.entries(options) as [keyof RouteCreationOptions, boolean][])
      .filter(([, v]) => v)
      .map(([k]) => optionLabels[k])
      .join(', ') || 'None';

  // Cleanup map markers on unmount
  useEffect(() => {
    return () => {
      useMapStore.getState().setDeparturePortId(null);
      useMapStore.getState().setArrivalPortId(null);
    };
  }, []);

  function handleSetDeparture(port: Port) {
    setDeparturePort(port);
    setDeparturePortId(port.id);
  }

  function handleSetArrival(port: Port) {
    setArrivalPort(port);
    setArrivalPortId(port.id);
  }

  function handlePortLocate(port: Port) {
    if (port.id !== focusedPortId) {
      setFocusedPortId(port.id);
      setPanToCoordinate({ lon: port.lon, lat: port.lat });
    } else {
      setPanToCoordinate({ lon: port.lon, lat: port.lat, zoomIn: true });
    }
  }

  function handleToggleOption(key: keyof RouteCreationOptions) {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function handleCreate() {
    if (!departurePort || !arrivalPort) return;

    await createRoute.mutateAsync({
      name: routeName || autoName,
      departurePortId: departurePort.id,
      arrivalPortId: arrivalPort.id,
      options,
    });

    onBack();
  }

  return (
    <PanelContent>
      <div
        data-testid="route-creation-panel"
        className="flex flex-col gap-3"
        style={{ height: '100%' }}
      >
        {/* Create button */}
        <Button
          variant="contained"
          disabled={!departurePort || !arrivalPort || createRoute.isPending}
          onClick={() => void handleCreate()}
          startIcon={createRoute.isPending ? <CircularProgress size={16} /> : undefined}
          sx={{ textTransform: 'none' }}
        >
          {createRoute.isPending ? 'Creating...' : 'Create Route'}
        </Button>

        {/* Route Name — inline editable */}
        {isEditingName ? (
          <TextField
            size="small"
            value={routeName}
            onChange={(e) => setRouteName(e.target.value)}
            onBlur={() => setIsEditingName(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setIsEditingName(false);
            }}
            autoFocus
            fullWidth
            placeholder="New Route"
            slotProps={{ input: { sx: { fontSize: '1rem', fontWeight: 600 } } }}
          />
        ) : (
          <Typography
            variant="subtitle1"
            fontWeight={600}
            onClick={() => setIsEditingName(true)}
            sx={{
              cursor: 'pointer',
              color: routeName || autoName ? 'text.primary' : 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            {routeName || autoName || 'New Route'}
          </Typography>
        )}

        {/* Port blocks — departure (left) and arrival (right) */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <PortBlock
            label="departure"
            port={departurePort}
            isFocused={focusedPortId === departurePort?.id}
            onClear={() => {
              setDeparturePort(null);
              setDeparturePortId(null);
            }}
            onLocate={handlePortLocate}
          />
          <PortBlock
            label="arrival"
            port={arrivalPort}
            isFocused={focusedPortId === arrivalPort?.id}
            onClear={() => {
              setArrivalPort(null);
              setArrivalPortId(null);
            }}
            onLocate={handlePortLocate}
          />
        </Box>

        {/* Route Options — collapsible */}
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            onClick={() => setOptionsExpanded(!optionsExpanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              py: 0.75,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                Route Options
              </Typography>
              {!optionsExpanded && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {selectedOptionsLabel}
                </Typography>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {optionsExpanded ? '\u25BE' : '\u25B8'}
            </Typography>
          </Box>
          {optionsExpanded && (
            <Box sx={{ px: 1.5, pb: 1, display: 'flex', flexDirection: 'column' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={options.allowSuezCanal}
                    onChange={() => handleToggleOption('allowSuezCanal')}
                  />
                }
                label={<Typography variant="body2">Allow Suez Canal</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={options.allowPanamaCanal}
                    onChange={() => handleToggleOption('allowPanamaCanal')}
                  />
                }
                label={<Typography variant="body2">Allow Panama Canal</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={options.allowBeringStrait}
                    onChange={() => handleToggleOption('allowBeringStrait')}
                  />
                }
                label={<Typography variant="body2">Allow Bering Strait</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={options.allowRivers}
                    onChange={() => handleToggleOption('allowRivers')}
                  />
                }
                label={<Typography variant="body2">Allow Rivers</Typography>}
              />
            </Box>
          )}
        </Box>

        {/* Port search */}
        <TextField
          size="small"
          placeholder="Search ports (min 2 chars)..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (e.target.value.length < 2) setFocusedPortId(null);
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          fullWidth
        />

        {/* Port list */}
        {portsLoading && searchQuery.length >= 2 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!portsLoading && ports.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            {ports.map((port) => (
              <PortCard
                key={port.id}
                port={port}
                isDeparture={departurePort?.id === port.id}
                isArrival={arrivalPort?.id === port.id}
                isFocused={focusedPortId === port.id}
                onSetDeparture={handleSetDeparture}
                onSetArrival={handleSetArrival}
                onLocate={handlePortLocate}
              />
            ))}
          </Box>
        )}

        {!portsLoading && searchQuery.length >= 2 && ports.length === 0 && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 1 }}>
            No ports found
          </Typography>
        )}

        {searchQuery.length < 2 && ports.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              borderRadius: 1,
              bgcolor: (t) => alpha(t.palette.info.main, 0.08),
              border: 1,
              borderColor: (t) => alpha(t.palette.info.main, 0.3),
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Type at least 2 characters to search for ports
            </Typography>
          </Box>
        )}
      </div>
    </PanelContent>
  );
}

interface PortBlockProps {
  label: 'departure' | 'arrival';
  port: Port | null;
  isFocused: boolean;
  onClear: () => void;
  onLocate: (port: Port) => void;
}

function PortBlock({ label, port, isFocused, onClear, onLocate }: PortBlockProps) {
  const borderColor = label === 'departure' ? 'success.main' : 'info.main';

  return (
    <Box
      sx={{
        flex: '1 1 0',
        minWidth: 0,
        position: 'relative',
        border: 1,
        borderColor: port ? borderColor : 'divider',
        borderRadius: 1,
        px: 1.5,
        py: 1,
        minHeight: 48,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {port ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <IconButton
              size="small"
              onClick={() => onLocate(port)}
              aria-label={`Locate ${port.name}`}
              sx={{ p: 0, flexShrink: 0 }}
            >
              <MyLocationIcon sx={{ fontSize: 12 }} color={isFocused ? 'info' : 'inherit'} />
            </IconButton>
            <Tooltip title={port.name} enterDelay={400} placement="top">
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                sx={{
                  fontSize: '0.73rem',
                  cursor: 'help',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {port.name}
              </Typography>
            </Tooltip>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ fontSize: '0.63rem', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {port.country}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ fontSize: '0.63rem', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {port.locode}
          </Typography>
          <IconButton
            size="small"
            onClick={onClear}
            aria-label={`Clear ${label}`}
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              p: 0.25,
            }}
          >
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Choose {label} port
        </Typography>
      )}
    </Box>
  );
}

interface PortCardProps {
  port: Port;
  isDeparture: boolean;
  isArrival: boolean;
  isFocused: boolean;
  onSetDeparture: (port: Port) => void;
  onSetArrival: (port: Port) => void;
  onLocate: (port: Port) => void;
}

function PortCard({
  port,
  isDeparture,
  isArrival,
  isFocused,
  onSetDeparture,
  onSetArrival,
  onLocate,
}: PortCardProps) {
  const isSelected = isDeparture || isArrival;
  const selectedColor = isDeparture ? 'success' : isArrival ? 'info' : undefined;

  return (
    <Card
      variant="outlined"
      sx={{
        flexShrink: 0,
        ...(isSelected &&
          selectedColor && {
            borderColor: `${selectedColor}.main`,
            bgcolor: (t) => alpha(t.palette[selectedColor].main, 0.08),
          }),
      }}
    >
      <CardContent sx={{ py: 0.75, px: 1.5, '&:last-child': { pb: 0.75 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => onLocate(port)}
                aria-label={`Locate ${port.name}`}
                sx={{ p: 0, flexShrink: 0 }}
              >
                <MyLocationIcon sx={{ fontSize: 14 }} color={isFocused ? 'info' : 'inherit'} />
              </IconButton>
              <Tooltip title={port.name} enterDelay={400} placement="top">
                <Typography variant="body2" fontWeight={600} noWrap sx={{ cursor: 'help' }}>
                  {port.name}
                </Typography>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {port.country}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {port.locode}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
            <Button
              size="small"
              variant={isDeparture ? 'contained' : 'outlined'}
              color="success"
              disabled={isArrival}
              onClick={() => onSetDeparture(port)}
              sx={{
                textTransform: 'none',
                minWidth: 'auto',
                px: 1,
                py: 0.25,
                fontSize: '0.85rem',
                lineHeight: 1.3,
              }}
            >
              Departure
            </Button>
            <Button
              size="small"
              variant={isArrival ? 'contained' : 'outlined'}
              color="info"
              disabled={isDeparture}
              onClick={() => onSetArrival(port)}
              sx={{
                textTransform: 'none',
                minWidth: 'auto',
                px: 1,
                py: 0.25,
                fontSize: '0.85rem',
                lineHeight: 1.3,
              }}
            >
              Arrival
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
